/**
 * EntityModalShell — каркас окна сущности (макет, товар, черновик ПИМ, карточка МП).
 *
 * Берёт на себя то, что одинаково у всех окон: оверлей и геометрию, шапку
 * (метка типа, главный ID, бейджи, название, служебные номера), навигацию
 * «N из M», меню «Ещё», «Во весь экран», горячие клавиши, focus-trap, возврат
 * фокуса на источник, скелетоны на время загрузки, скроллируемое тело и футер.
 * Содержимое — целиком за вызывающей стороной.
 *
 * Клавиши обрабатывает только верхнее окно общего стека оверлеев: пока над
 * окном открыт диалог, лайтбокс или вложенное окно, оно молчит.
 *
 * Прежнее имя — `CatalogCardModal` — остаётся тонкой обёрткой с тем же API.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../../lib/cn'
import { CopyButton } from '../ui/CopyButton'
import { copyToClipboard } from '../ui/copyToClipboard'
import { IdMeta, type IdMetaItem } from '../ui/IdMeta'
import { useOverlayLayer } from '../ui/overlayStack'
import { EntityShellLayoutContext, useNarrowScreen } from './layoutContext'
import { Skeleton } from '../ui/Skeleton'
import { useToastStore } from '../../stores/toastStore'

export interface EntityMoreMenuAction {
  key: string
  label: string
  icon?: React.ReactNode
  onClick: () => void
  /** Опасное действие: красным и в конце меню. */
  danger?: boolean
  disabled?: boolean
  /** Почему пункт неактивен: показывается подсказкой. */
  disabledReason?: string
  /** Подпись горячей клавиши справа. */
  hotkey?: string
}

export type EntityMoreMenuItem = EntityMoreMenuAction | { divider: true }

export interface EntityPrimaryId {
  value: string
  /** Название значения для тоста и подписи кнопки: «артикул», «код макета». */
  label?: string
  /** Ссылка на окно: копируется по Shift+C и Shift+клик. */
  link?: string
}

export interface EntityModalShellProps {
  isOpen: boolean
  /** Подпись диалога для скринридера */
  ariaLabel: string
  /** Прежняя шапка: верхняя строка (идентификатор, копирование, бейджи). Не нужна, если заданы primaryId/title/meta. */
  header?: React.ReactNode
  /** Прежняя шапка: вторая строка (название, категория, ссылки). */
  subheader?: React.ReactNode
  /** Метка типа над заголовком: «Черновик ВБ». */
  kind?: { label: string; icon?: LucideIcon }
  /** Главный ID: моноширинный, крупный, с копированием. C — копировать, Shift+C — ссылку. */
  primaryId?: EntityPrimaryId
  /** До трёх бейджей рядом с главным ID. */
  badges?: React.ReactNode
  /** Название одной строкой с обрезкой. */
  title?: React.ReactNode
  /** Строка служебных номеров с копированием. */
  meta?: IdMetaItem[]
  /** Действия справа перед навигацией: меню карточки и точечное обновление. */
  toolbar?: React.ReactNode
  /** Меню «Ещё»: опасные пункты уходят вниз и красятся. */
  moreMenu?: EntityMoreMenuItem[]
  /** Ключи всех строк текущей страницы — для навигации «N из M» */
  keys: string[]
  /** Ключ открытой карточки */
  activeKey: string | null
  /** Сколько всего строк в выборке, если больше загруженных: «N из M» показывает M. */
  totalCount?: number
  onNavigate: (key: string) => void
  onClose: () => void
  /** Цепочка связанных сущностей: рисуется под шапкой. */
  chain?: React.ReactNode
  /** Полоса вкладок — рисуется под шапкой, вне области скролла */
  tabs?: React.ReactNode
  /** Клавиши 1…9 выбирают вкладку. */
  tabsHotkeys?: { keys: string[]; onSelect: (key: string) => void }
  /** Полоса «Что дальше» между шапкой и рабочей областью. */
  nextStep?: React.ReactNode
  /** Синоним `nextStep`, оставлен для прежних окон. */
  stageBar?: React.ReactNode
  /** Необязательная постоянная колонка 19rem. */
  sidebar?: React.ReactNode
  /** Плашка над телом: ошибка карточки, предупреждение */
  banner?: React.ReactNode
  footer?: React.ReactNode
  children: React.ReactNode
  /**
   * Данные ещё не пришли: вместо полосы «Что дальше», колонки и тела
   * рисуются скелетоны, футер скрыт. Любой вывод до загрузки был бы ложным.
   */
  loading?: boolean
  /**
   * Отключает перехват клавиш на время вложенного диалога или лайтбокса.
   * Верхнее окно стека и так молчит под чужим оверлеем; флаг нужен там, где
   * диалог не зарегистрирован в стеке.
   */
  suspendHotkeys?: boolean
  /** Ctrl+S (работает и в полях ввода). */
  onSave?: () => void
  /** Клавиша E: переключить правку. */
  onToggleEdit?: () => void
  /** Кнопка и клавиша F «Во весь экран». По умолчанию включено. */
  allowFullscreen?: boolean
  /** Есть куда возвращаться: Alt+← и кнопка «Назад». */
  canGoBack?: boolean
  onBack?: () => void
  /**
   * Окно открыто поверх другого окна (товар из карточки макета): встаёт выше
   * него вместе с собственным затемнением, как `Modal layer="nested"`.
   */
  layer?: 'default' | 'nested'
}

export const FULLSCREEN_STORAGE_KEY = 'entityView.fullscreen'

const readFullscreen = (): boolean => {
  try {
    return localStorage.getItem(FULLSCREEN_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const writeFullscreen = (value: boolean): void => {
  try {
    localStorage.setItem(FULLSCREEN_STORAGE_KEY, value ? '1' : '0')
  } catch {
    // приватный режим или квота: окно просто забудет выбор
  }
}

const isEditableTarget = (target: HTMLElement | null): boolean =>
  Boolean(
    target &&
      (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName ?? '') ||
        target.isContentEditable ||
        target.getAttribute?.('contenteditable') === 'true'),
  )

/** Буква независимо от раскладки: `key` в русской раскладке даёт «с», `code` — KeyC. */
const isLetter = (event: KeyboardEvent, letter: string): boolean =>
  event.key?.toLowerCase() === letter || event.code === `Key${letter.toUpperCase()}`

const formatCount = (value: number): string => value.toLocaleString('ru-RU')

const navButton =
  'rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-200'

export const EntityModalShell: React.FC<EntityModalShellProps> = ({
  isOpen,
  ariaLabel,
  header,
  subheader,
  kind,
  primaryId,
  badges,
  title,
  meta,
  toolbar,
  moreMenu,
  keys,
  activeKey,
  totalCount,
  onNavigate,
  onClose,
  chain,
  tabs,
  tabsHotkeys,
  nextStep,
  stageBar,
  sidebar,
  banner,
  footer,
  children,
  loading,
  suspendHotkeys,
  onSave,
  onToggleEdit,
  allowFullscreen = true,
  canGoBack,
  onBack,
  layer = 'default',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuOpenRef = useRef(false)
  menuOpenRef.current = menuOpen
  const [fullscreen, setFullscreen] = useState<boolean>(readFullscreen)
  // Ниже 960 px окно перестраивается: на весь экран, колонка над вкладками, скролл целиком.
  const narrow = useNarrowScreen()
  const { isTopmost } = useOverlayLayer(isOpen)

  const index = activeKey ? keys.indexOf(activeKey) : -1
  const hasPrev = index > 0
  const hasNext = index >= 0 && index < keys.length - 1

  const toggleFullscreen = useCallback(() => {
    setFullscreen((current) => {
      const next = !current
      writeFullscreen(next)
      return next
    })
  }, [])

  const copyPrimary = useCallback(
    async (asLink: boolean) => {
      if (!primaryId) return
      const text = asLink && primaryId.link ? primaryId.link : primaryId.value
      const ok = await copyToClipboard(text)
      const { showToast } = useToastStore.getState()
      if (!ok) showToast('Не удалось скопировать', 'error')
      else showToast(`Скопировано: ${asLink && primaryId.link ? 'ссылка' : primaryId.label ?? primaryId.value}`, 'success')
    },
    [primaryId],
  )

  // Клавиши. Ни одна, кроме Esc и Ctrl+S, не срабатывает в полях ввода.
  useEffect(() => {
    if (!isOpen || suspendHotkeys) return
    const onKey = (event: KeyboardEvent) => {
      if (!isTopmost()) return
      const target = event.target as HTMLElement | null
      const inInput = isEditableTarget(target)

      if (event.key === 'Escape') {
        if (menuOpenRef.current) {
          setMenuOpen(false)
          return
        }
        // В поле ввода первое Esc только снимает фокус: окно с набранным текстом
        // не должно закрываться одним нажатием. Вне поля — закрываем.
        if (inInput) {
          target?.blur?.()
          return
        }
        onClose()
        return
      }

      if ((event.ctrlKey || event.metaKey) && !event.altKey && isLetter(event, 's')) {
        if (onSave) {
          event.preventDefault()
          onSave()
        }
        return
      }

      if (inInput) return

      if (event.altKey && event.key === 'ArrowLeft') {
        if (canGoBack && onBack) {
          event.preventDefault()
          onBack()
        }
        return
      }
      if (event.ctrlKey || event.metaKey || event.altKey) return

      if (event.key === 'ArrowLeft' && hasPrev) {
        onNavigate(keys[index - 1])
        return
      }
      if (event.key === 'ArrowRight' && hasNext) {
        onNavigate(keys[index + 1])
        return
      }

      if (isLetter(event, 'c') && primaryId) {
        void copyPrimary(event.shiftKey)
        return
      }
      if (event.shiftKey) return
      if (isLetter(event, 'f') && allowFullscreen) {
        toggleFullscreen()
        return
      }
      if (isLetter(event, 'e') && onToggleEdit) {
        onToggleEdit()
        return
      }
      if (tabsHotkeys && /^[1-9]$/.test(event.key)) {
        const tabKey = tabsHotkeys.keys[Number(event.key) - 1]
        if (tabKey) tabsHotkeys.onSelect(tabKey)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [
    isOpen,
    suspendHotkeys,
    isTopmost,
    hasPrev,
    hasNext,
    index,
    keys,
    onNavigate,
    onClose,
    onSave,
    onToggleEdit,
    onBack,
    canGoBack,
    primaryId,
    copyPrimary,
    allowFullscreen,
    toggleFullscreen,
    tabsHotkeys,
  ])

  // Меню «Ещё» закрывается кликом вне его.
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  // Фокус: при открытии — в диалог, при закрытии — обратно на источник
  useEffect(() => {
    if (isOpen) {
      openerRef.current = document.activeElement as HTMLElement | null
      dialogRef.current?.focus()
    } else if (openerRef.current) {
      openerRef.current.focus?.()
      openerRef.current = null
    }
  }, [isOpen])

  // Focus-trap: Tab не уходит за пределы окна
  useEffect(() => {
    if (!isOpen || suspendHotkeys) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !isTopmost()) return
      const root = dialogRef.current
      if (!root) return
      const focusables = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, suspendHotkeys, isTopmost])

  if (!isOpen) return null

  const structuredHeader = Boolean(primaryId || title || (meta && meta.length > 0))
  const bar = nextStep ?? stageBar
  const KindIcon = kind?.icon

  // Опасные пункты — последними, перед ними разделитель.
  const menuItems: EntityMoreMenuItem[] = (() => {
    if (!moreMenu || moreMenu.length === 0) return []
    const normal = moreMenu.filter((item) => 'divider' in item || !item.danger)
    const danger = moreMenu.filter((item): item is EntityMoreMenuAction => !('divider' in item) && Boolean(item.danger))
    return danger.length > 0 ? [...normal, { divider: true as const }, ...danger] : normal
  })()

  const backButton =
    canGoBack && onBack && !chain ? (
      <button
        type="button"
        title="Назад (Alt+←)"
        aria-label="Назад"
        onClick={onBack}
        className="mr-1 inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Назад
      </button>
    ) : null

  const skeletonBar = (
    <div className="shrink-0 space-y-2.5 border-b border-gray-200 px-5 py-3 dark:border-zinc-800" aria-busy="true">
      <Skeleton variant="custom" className="h-4 w-1/2" />
      <div className="flex gap-1.5">
        <Skeleton variant="custom" className="h-6 w-24 rounded-full" />
        <Skeleton variant="custom" className="h-6 w-28 rounded-full" />
        <Skeleton variant="custom" className="h-6 w-20 rounded-full" />
      </div>
    </div>
  )
  const skeletonSidebar = (
    <div className="space-y-3" aria-busy="true">
      <Skeleton variant="custom" className="mx-auto aspect-[3/4] w-full max-w-[15rem] rounded-xl" />
      <div className="grid grid-cols-2 gap-2">
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} variant="custom" className="h-12 rounded-md" />
        ))}
      </div>
    </div>
  )
  const skeletonBody = (
    <div className="space-y-4" aria-busy="true">
      <Skeleton variant="custom" className="h-5 w-40" />
      <Skeleton variant="custom" className="h-10 w-full rounded-md" />
      <Skeleton variant="custom" className="h-5 w-32" />
      <Skeleton variant="custom" className="h-40 w-full rounded-md" />
    </div>
  )

  const body = loading ? skeletonBody : children

  return (
    <EntityShellLayoutContext.Provider value={{ narrow }}>
      <div
        className={cn('fixed inset-0 bg-black/40', layer === 'nested' ? 'z-[80]' : 'z-40')}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        data-layout={narrow ? 'narrow' : 'wide'}
        tabIndex={-1}
        /*
          Высота у окна фиксированная и без колонки, и с ней: иначе на вкладке
          с коротким содержимым оно съёживается, на длинной растёт, и
          переключение вкладок дёргает рамку. «Во весь экран» растягивает окно
          почти на весь экран с отступом 0,5 rem.
        */
        className={cn(
          'fixed flex flex-col overflow-hidden bg-white shadow-medium outline-none dark:bg-zinc-900',
          layer === 'nested' ? 'z-[90]' : 'z-50',
          narrow || (fullscreen && allowFullscreen)
            ? 'inset-2 rounded-xl'
            : cn(
                'left-1/2 top-1/2 h-[calc(100vh-3rem)] min-h-[41rem] -translate-x-1/2 -translate-y-1/2',
                sidebar
                  ? 'w-[min(76rem,calc(100vw-2rem))] rounded-xl'
                  : 'w-[min(72rem,calc(100vw-2rem))] rounded-lg',
              ),
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center gap-2.5 border-b border-gray-200 dark:border-zinc-800',
            narrow ? 'flex-wrap px-3 py-2.5' : 'px-5 py-3.5',
          )}
        >
          {backButton}
          {structuredHeader ? (
            <div className="min-w-0 flex-1">
              {kind && (
                <div className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                  {KindIcon && <KindIcon className="h-3 w-3" aria-hidden="true" />}
                  {kind.label}
                </div>
              )}
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                {primaryId && (
                  <>
                    <span className="truncate font-mono text-base font-semibold text-gray-900 dark:text-zinc-100">
                      {primaryId.value}
                    </span>
                    <CopyButton value={primaryId.value} label={primaryId.label} link={primaryId.link} />
                  </>
                )}
                {badges}
              </div>
              {title && (
                <div
                  className="mt-0.5 truncate text-sm text-gray-600 dark:text-zinc-300"
                  title={typeof title === 'string' ? title : undefined}
                >
                  {title}
                </div>
              )}
              {meta && meta.length > 0 && <IdMeta items={meta} className="mt-1" />}
            </div>
          ) : (
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">{header}</div>
              {subheader && (
                <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs">{subheader}</div>
              )}
            </div>
          )}
          <span className={cn('ml-auto flex items-center gap-1.5', narrow ? 'flex-wrap justify-end' : 'shrink-0')}>
            {toolbar}
            {menuItems.length > 0 && (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((open) => !open)}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  Ещё
                </button>
                {menuOpen && (
                  <div
                    role="menu"
                    aria-label="Ещё"
                    className="absolute right-0 top-full z-30 mt-1 min-w-[15rem] rounded-lg border border-gray-200 bg-white py-1 shadow-medium dark:border-zinc-700 dark:bg-zinc-900"
                  >
                    {menuItems.map((item, position) => {
                      if ('divider' in item) {
                        return (
                          <div
                            key={`divider-${position}`}
                            role="separator"
                            className="my-1 border-t border-gray-100 dark:border-zinc-800"
                          />
                        )
                      }
                      const blocked = Boolean(item.disabled || item.disabledReason)
                      return (
                        <button
                          key={item.key}
                          type="button"
                          role="menuitem"
                          disabled={blocked}
                          title={item.disabledReason}
                          onClick={() => {
                            setMenuOpen(false)
                            item.onClick()
                          }}
                          className={cn(
                            'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50',
                            item.danger
                              ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40'
                              : 'text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800',
                          )}
                        >
                          {item.icon && <span className="shrink-0">{item.icon}</span>}
                          <span className="flex-1">{item.label}</span>
                          {item.hotkey && (
                            <kbd className="rounded border border-gray-200 px-1 font-mono text-[10px] text-gray-400 dark:border-zinc-700 dark:text-zinc-500">
                              {item.hotkey}
                            </kbd>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
            {/*
              Стрелки и счётчик собраны в одну рамку: это один орган управления,
              а не три соседние кнопки. Ширина счётчика фиксирована, иначе
              «7 из 40» и «12 из 4 830» дёргают стрелки при листании.
            */}
            <span
              className="flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5 dark:border-zinc-700 dark:bg-zinc-800"
              aria-label="Навигация по карточкам"
            >
              <button
                type="button"
                title="Предыдущий (←)"
                aria-label="Предыдущий"
                disabled={!hasPrev}
                onClick={() => hasPrev && onNavigate(keys[index - 1])}
                className={navButton}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {index >= 0 && keys.length > 0 && (
                <span className="min-w-[4.5rem] px-1 text-center text-xs tabular-nums text-gray-600 dark:text-zinc-300">
                  {index + 1} из {formatCount(Math.max(totalCount ?? 0, keys.length))}
                </span>
              )}
              <button
                type="button"
                title="Следующий (→)"
                aria-label="Следующий"
                disabled={!hasNext}
                onClick={() => hasNext && onNavigate(keys[index + 1])}
                className={navButton}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </span>
            {allowFullscreen && (
              <button
                type="button"
                title={fullscreen ? 'Обычный размер (F)' : 'Во весь экран (F)'}
                aria-label={fullscreen ? 'Обычный размер' : 'Во весь экран'}
                aria-pressed={fullscreen}
                onClick={toggleFullscreen}
                className={navButton}
              >
                {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            )}
            <button
              type="button"
              title="Закрыть (Esc)"
              aria-label="Закрыть"
              onClick={onClose}
              className={navButton}
            >
              <X className="h-5 w-5" />
            </button>
          </span>
        </div>

        {chain}
        {banner}
        {loading ? skeletonBar : bar}
        {sidebar && narrow ? (
          /*
            Узкий экран: колонка над вкладками, прокрутка одна на всё — иначе
            две вложенные области не оставили бы телу места. Вкладки липнут
            к верху, пока листается тело.
          */
          <div
            data-testid="entity-shell-scroll"
            className="min-h-0 flex-1 overflow-y-auto bg-gray-50/70 dark:bg-zinc-950/30"
          >
            <aside className="border-b border-gray-200 p-3 dark:border-zinc-800">
              {loading ? skeletonSidebar : sidebar}
            </aside>
            {tabs && (
              <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-3 dark:border-zinc-800 dark:bg-zinc-900">
                {tabs}
              </div>
            )}
            <div className="bg-white px-3 py-4 dark:bg-zinc-900">{body}</div>
          </div>
        ) : sidebar ? (
          /*
            Две независимые прокрутки: колонка с фото и цифрами остаётся на
            месте, пока листается содержимое вкладки. При общем скролле опора
            уезжала вверх, и на длинной вкладке человек переставал видеть,
            какую карточку правит.
          */
          <div className="flex min-h-0 flex-1 bg-gray-50/70 dark:bg-zinc-950/30">
            <aside className="w-[19rem] shrink-0 overflow-y-auto border-r border-gray-200 p-4 dark:border-zinc-800">
              {loading ? skeletonSidebar : sidebar}
            </aside>
            <div className="flex min-w-0 flex-1 flex-col">
              {tabs && (
                <div className="shrink-0 border-b border-gray-200 bg-white px-5 dark:border-zinc-800 dark:bg-zinc-900">
                  {tabs}
                </div>
              )}
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{body}</div>
            </div>
          </div>
        ) : (
          <>
            {tabs && (
              <div className={cn('shrink-0 border-b border-gray-200 dark:border-zinc-800', narrow ? 'px-3' : 'px-5')}>
                {tabs}
              </div>
            )}
            <div className={cn('min-h-0 flex-1 overflow-y-auto py-4', narrow ? 'px-3' : 'px-5')}>{body}</div>
          </>
        )}

        {footer && !loading && (
          <div
            className={cn(
              'flex shrink-0 items-center gap-2 border-t border-gray-200 dark:border-zinc-800',
              narrow ? 'flex-wrap px-3 py-2' : 'px-5 py-3',
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </EntityShellLayoutContext.Provider>
  )
}

export default EntityModalShell
